import type {
  CamaRuntime,
  CamaSurrenderProductType,
} from 'src/apis/surrender-service/types';
import type { WizardState } from 'src/components/wizard/state';
import type { ConstraintViolation } from 'src/utils/is-constraint-violation';

export const CAMA_NO_AUCTION_PUBLISHED_MESSAGE =
  'Within the next 30 days there is no auction published for the product type you selected. Please select another product type.';

export const getCamaSurrenderRequestWizardName = (
  transactionId: string
): string => `surrender-request:cama:${transactionId}`;

export const getCreateCamaSurrenderRequestStepPaths = (
  transactionId: string
) => {
  const base = `/reporting/my-transactions/surrender/create/cama/${transactionId}`;
  return {
    specify: base,
    confirm: `${base}/confirm`,
    success: `${base}/success`,
  };
};

type SpecifyStepData = {
  productType: CamaSurrenderProductType | null;
  auctionScheduleId: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  capacity: number | null;
  rollIn: boolean;
  constraintViolation?: ConstraintViolation;
};

type ConfirmStepData = Record<string, never>;

type SuccessStepData = {
  assignedSurrenderId: string;
  surrenderRequestId: string;
};

export type CreateCamaSurrenderRequestWizardStepsData = [
  SpecifyStepData,
  ConfirmStepData,
  SuccessStepData,
];

export type CreateCamaSurrenderRequestWizard =
  WizardState<CreateCamaSurrenderRequestWizardStepsData>;

export function getInitialProductType(
  productTypes: CamaSurrenderProductType[]
): CamaSurrenderProductType | null {
  if (productTypes.includes('MONTH')) return 'MONTH';
  if (productTypes.length === 1) return productTypes[0] ?? null;
  return null;
}

function isSameRuntime(first: CamaRuntime, second: CamaRuntime): boolean {
  return (
    first.auctionScheduleId === second.auctionScheduleId &&
    first.runtimeStart === second.runtimeStart &&
    first.runtimeEnd === second.runtimeEnd
  );
}

export function findMatchingRuntime(
  items: CamaRuntime[],
  selection: {
    auctionScheduleId: string | null;
    periodStart: string | null;
    periodEnd: string | null;
  }
): CamaRuntime | undefined {
  const { auctionScheduleId, periodStart, periodEnd } = selection;
  if (auctionScheduleId == null || periodStart == null || periodEnd == null) {
    return undefined;
  }

  return items.find((item) =>
    isSameRuntime(item, {
      auctionScheduleId,
      runtimeStart: periodStart,
      runtimeEnd: periodEnd,
    })
  );
}
